import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting contact form:', err);
      alert('Failed to send message. Please email us directly at hello@subsurfaceconsulting.co');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] flex flex-col">
      <SEO
        title="Contact | Subsurface — Trust & Risk Architecture"
        description="Get in touch with Subsurface for a Structured Review (£295), Pre-Release QA Audit (£750) or Advisory Retainer (£500/month). Async-first, no calls required."
        path="/contact"
      />
      <Navigation />

      <div className="flex-1 pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold font-display mb-6">Get in Touch</h1>
            <p className="text-lg text-[#A0A0A0] leading-relaxed">
              Ready to surface hidden trust gaps? Let's talk.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-8 rounded-lg"
          >
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Company</label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-3 bg-[#1A1A1D] border border-[#FF6A00]/10 rounded-md text-white focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00] transition-colors"
                    placeholder="Tell us about your trust and revenue integrity concerns..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#FF6A00] text-white font-semibold rounded-md hover:bg-[#FF6A00]/90 transition-all disabled:opacity-50"
                >
                  {loading ? 'Sending...' : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 text-[#FF6A00] mx-auto mb-4" />
                <h3 className="text-2xl font-display font-semibold mb-2">Message Sent</h3>
                <p className="text-[#A0A0A0] mb-4">
                  We've received your submission. A structured review may follow.
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-6">
                  <a href="/" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">Return to Home</a>
                  <a href="/scan" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">Run Scan</a>
                  <a href="/pricing" className="text-sm text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">View Pricing</a>
                </div>
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-white/5 text-center">
              <p className="text-sm text-[#A0A0A0] mb-2">Or email us directly:</p>
              <a href="mailto:hello@subsurfaceconsulting.co" className="text-[#FF6A00] hover:text-[#FF6A00]/80 transition-colors">
                hello@subsurfaceconsulting.co
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
